import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import {
  OperatorWithOverrides,
  useWidgetUpdateByIdMutation,
  WidgetByIdDocument,
  WidgetByIdQuery,
} from "gql/generated";
import { createMultilingual, getMultilingualString } from "shared/utils";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Widgets]);

export const widgetOperatorOverridesImageSchema = z.object({
  _id: z.string(),
  caption: z.object({
    en: z.string().nullable().optional(),
    es: z.string().nullable().optional(),
    fr: z.string().nullable().optional(),
  }),
  blurhash: z.string(),
});

export const widgetOperatorOverridesSchema = z.object({
  shortDescription: z
    .string({
      required_error: `${i18n.t("widgets:DETAILS.FORM.LABELS.CARD_TEXT")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("widgets:DETAILS.FORM.LABELS.CARD_TEXT")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  description: z
    .string({
      required_error: `${i18n.t("widgets:DETAILS.FORM.LABELS.DESCRIPTION")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("widgets:DETAILS.FORM.LABELS.DESCRIPTION")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  images: z.array(widgetOperatorOverridesImageSchema),
});

export type WidgetOperatorOverridesFormValues = z.infer<
  typeof widgetOperatorOverridesSchema
>;

type UseWidgetOperatorOverridesFormProps = {
  widget: WidgetByIdQuery["widgetById"];
  operator?: OperatorWithOverrides;
  onSuccess: () => void;
  onError: () => void;
};

export const useWidgetOperatorOverridesForm = ({
  widget,
  operator,
  onSuccess,
  onError,
}: UseWidgetOperatorOverridesFormProps) => {
  // Create an image array that we can then convert into the zod array
  const images =
    operator?.overrides?.images && operator.overrides.images.length > 0
      ? operator.overrides.images
      : (operator?.images ?? []);

  const defaultValues: WidgetOperatorOverridesFormValues = {
    shortDescription: getMultilingualString(
      operator?.overrides?.shortDescription ?? operator?.shortDescription ?? {},
    ),
    description: getMultilingualString(
      operator?.overrides?.description ?? operator?.description ?? {},
    ),
    images: images.map((image) =>
      widgetOperatorOverridesImageSchema.parse(image),
    ),
  };

  const [widgetUpdateByIdMutation] = useWidgetUpdateByIdMutation({
    refetchQueries: [WidgetByIdDocument],
    awaitRefetchQueries: true,
  });

  return useFormHandler<WidgetOperatorOverridesFormValues>(
    useCallback(
      async (data) => {
        if (data && operator) {
          // We need to determine if we are sending an update or deletion
          // We can delete an override by sending JUST the _entityId

          // The description is different if the form data differs from the operator.description
          const description =
            data.description ===
            getMultilingualString(operator.description ?? {})
              ? undefined
              : { description: createMultilingual(data.description ?? "") };

          // The images are different if the ORDER of items (by checking the _id) in the form
          // is different than the operator.images array order
          const images = data.images?.every(
            (image, i) => image?._id === operator.images?.[i]._id,
          )
            ? undefined
            : { images: data.images?.map((image) => image?._id ?? "") };

          // The shortDescription is different if the form data differs from the operator.shortDescription
          const shortDescription =
            data.shortDescription ===
            getMultilingualString(operator.shortDescription ?? {})
              ? undefined
              : {
                  shortDescription: createMultilingual(
                    data.shortDescription ?? "",
                  ),
                };

          await widgetUpdateByIdMutation({
            variables: {
              id: widget._id,
              record: {
                entityOverrides: [
                  {
                    _entityId: operator._entityId,
                    ...description,
                    ...images,
                    ...shortDescription,
                  },
                ],
              },
            },
          })
            .then((response) => (response.data ? onSuccess() : onError()))
            .catch(() => onError());
        }
      },
      [operator, widget, widgetUpdateByIdMutation, onError, onSuccess],
    ),
    defaultValues,
    { schema: widgetOperatorOverridesSchema, mode: "onBlur" },
  );
};
